// The board for an external screen (bör 2.7): per pool, which cars are free now, which are out
// and until when, the next booking, and the day's count. Large type, refreshed every minute.
import { useQuery } from '@tanstack/react-query';
import { sv } from '@aldorock-fordon/shared/sv';
import { api } from '../api.ts';
import { usePage } from '../page.ts';
import { clock, plate } from '../format.ts';

type Board = {
  updatedAt: string;
  fleet: { vehicles: number; inspections: number; keysOut: number };
  pools: Array<{
    id: string;
    name: string;
    garage: string | null;
    free: number;
    out: number;
    today: number;
    cars: Array<{
      vehicleId: string;
      plate: string;
      make: string | null;
      model: string | null;
      keyPresent: boolean | null;
      now: { driver: string; until: string } | null;
      next: { driver: string; from: string } | null;
    }>;
  }>;
};

export function BoardPage() {
  const h1 = usePage(sv.board.title);
  const q = useQuery({
    queryKey: ['board'],
    queryFn: () => api<Board>('/board'),
    refetchInterval: 60_000,
  });
  const d = q.data;
  return (
    <div className="board">
      <h1 ref={h1} tabIndex={-1}>
        {sv.board.title}
      </h1>
      <p className="muted">{sv.board.intro}</p>
      {d ? (
        <>
          <p className="caption" role="status">
            {sv.board.updated(clock(d.updatedAt))}. {sv.board.fleet}: {d.fleet.vehicles}{' '}
            {sv.board.vehicles.toLowerCase()}, {d.fleet.inspections}{' '}
            {sv.board.inspections.toLowerCase()}, {d.fleet.keysOut} {sv.board.keyOut.toLowerCase()}.
          </p>
          <div className="board-pools">
            {d.pools.map((p) => (
              <section key={p.id} className="board-pool" aria-labelledby={`board-${p.id}`}>
                <h2 id={`board-${p.id}`}>{p.name}</h2>
                <p className="board-figures">
                  <span>
                    <b className="num">{p.free}</b> {sv.board.free.toLowerCase()}
                  </span>
                  <span>
                    <b className="num">{p.out}</b> {sv.board.out.toLowerCase()}
                  </span>
                  <span>
                    <b className="num">{p.today}</b> {sv.board.today.toLowerCase()}
                  </span>
                </p>
                <ul className="plain board-cars">
                  {p.cars.map((c) => (
                    <li key={c.vehicleId} className={c.now ? 'is-out' : 'is-free'}>
                      <span className="board-car">
                        {[c.make, c.model].filter(Boolean).join(' ')}{' '}
                        <span className="num">{plate(c.plate)}</span>
                      </span>
                      <span className="board-state">
                        {c.now
                          ? `${sv.board.out} ${sv.board.until(clock(c.now.until))}, ${c.now.driver}`
                          : c.next
                            ? `${sv.board.free}, ${sv.board.next.toLowerCase()} ${sv.board.from(clock(c.next.from))}`
                            : sv.board.free}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </>
      ) : (
        <p role="status">{sv.app.loading}</p>
      )}
    </div>
  );
}
